// PR-J — TPS fee-schedule editor (opened from the oversight directory). Lists
// the current effective-dated fee rows, adds a new row (append-only; the
// server rejects an effectiveFrom not strictly after the prior open row for
// the same feeType), and edits the collections-recovery rate. Percent fees +
// the recovery rate are FRACTIONS on the wire — edited here as percent
// (×100 display / ÷100 save), the broker-fee / card-surcharge convention.
// Mutating controls are gated on Tps.ManageFeeSchedule.

import { useState } from "react";
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  SubmitButton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  useToast,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import {
  formatCalendarDate,
  calendarDateInputToIso,
  isoDateInTz,
} from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  useCreateTpsFeeSchedule,
  useSetCollectionsRecoveryRate,
  useTpsFeeSchedule,
  useUpdateTpsFeeSchedule,
} from "@/hooks/portfolioAdmin/useTpsOversight";
import type {
  TpsFeeAmountType,
  TpsFeeScheduleDto,
  TpsFeeType,
} from "@/api/portfolioAdmin/tpsOversightApi";

const FEE_TYPES: { value: TpsFeeType; label: string }[] = [
  { value: "BoardingFee", label: "Boarding fee" },
  { value: "MonthlyServicingFee", label: "Monthly servicing fee" },
  { value: "PerPaymentFee", label: "Per-payment fee" },
  { value: "LateFee", label: "Late fee" },
  { value: "NsfFee", label: "NSF fee" },
  { value: "PayoffFee", label: "Payoff fee" },
  { value: "ModificationFee", label: "Modification fee" },
  { value: "DocRetrievalFee", label: "Doc retrieval fee" },
  { value: "YearEndFee", label: "Year-end fee" },
  { value: "AchWireFee", label: "ACH / wire fee" },
];

export const FEE_TYPE_LABEL: Record<TpsFeeType, string> = FEE_TYPES.reduce(
  (acc, t) => {
    acc[t.value] = t.label;
    return acc;
  },
  {} as Record<TpsFeeType, string>,
);

// Collection mode (the engine's DeductFromPayment / ChargeToBorrower modes),
// captured for every fee type — only the monthly servicing fee consults it at
// runtime today; the rest record the intent. Null on the wire means
// deduct_from_payment.
export const FEE_MODE_OPTIONS = [
  {
    value: "deduct_from_payment",
    label: "Deducted from the borrower's payment",
  },
  { value: "charge_to_borrower", label: "Charged to the borrower on top" },
] as const;

export const FEE_MODE_LABEL: Record<string, string> = FEE_MODE_OPTIONS.reduce(
  (acc, o) => {
    acc[o.value] = o.label;
    return acc;
  },
  {} as Record<string, string>,
);

function formatAmount(row: TpsFeeScheduleDto): string {
  return row.amountType === "Percent"
    ? `${(row.amount * 100).toLocaleString("en-US", {
        maximumFractionDigits: 4,
      })}%`
    : formatCurrency(row.amount, { maximumFractionDigits: 2 });
}

export function TpsFeeScheduleModal({
  tpsId,
  tpsName,
  currentRecoveryRate,
  open,
  onClose,
  editRow,
}: {
  tpsId: string;
  tpsName: string;
  currentRecoveryRate: number | null;
  open: boolean;
  onClose: () => void;
  /** When set, the fee form opens in edit mode for this row. */
  editRow?: TpsFeeScheduleDto;
}) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Tps.ManageFeeSchedule");
  const query = useTpsFeeSchedule(tpsId, open);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Fee schedule — ${tpsName}`}
      description="Effective-dated servicing fees and the collections-recovery rate for this servicer."
      size="lg"
      footer={
        <Button variant="text" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-space-5" data-testid="tps-fee-modal">
        <section>
          <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
            Current fees
          </h3>
          {query.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : query.isError ? (
            <p className="text-small text-status-error" role="alert">
              Couldn't load the fee schedule. Try again.
            </p>
          ) : !query.data || query.data.length === 0 ? (
            <EmptyState
              title="No fees configured"
              description="This servicer has no fee-schedule rows yet."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Fee</TH>
                  <TH className="text-right">Amount</TH>
                  <TH>Effective from</TH>
                  <TH>Effective to</TH>
                  <TH>Notes</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.map((row) => (
                  <TR key={row.id} data-testid={`tps-fee-row-${row.id}`}>
                    <TD className="font-semibold text-onyx">
                      {FEE_TYPE_LABEL[row.feeType] ?? row.feeType}
                      {row.effectiveTo === null && (
                        <Badge status="gold" className="ml-space-2">
                          Open
                        </Badge>
                      )}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {formatAmount(row)}
                    </TD>
                    <TD className="text-gray-mid">
                      {formatCalendarDate(row.effectiveFrom)}
                    </TD>
                    <TD className="text-gray-mid">
                      {row.effectiveTo
                        ? formatCalendarDate(row.effectiveTo)
                        : "—"}
                    </TD>
                    <TD className="text-gray-mid">{row.notes ?? "—"}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </section>

        {canManage ? (
          <>
            <AddFeeForm
              key={editRow?.id ?? "new"}
              tpsId={tpsId}
              editRow={editRow}
              onClose={onClose}
            />
            <RecoveryRateForm
              tpsId={tpsId}
              currentRate={currentRecoveryRate}
            />
          </>
        ) : (
          <p className="text-small text-gray-mid">
            You don't have permission to edit this fee schedule.
          </p>
        )}
      </div>
    </Modal>
  );
}

function AddFeeForm({
  tpsId,
  editRow,
  onClose,
}: {
  tpsId: string;
  editRow?: TpsFeeScheduleDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateTpsFeeSchedule(tpsId);
  const update = useUpdateTpsFeeSchedule(tpsId);
  const isEdit = !!editRow;

  // FeeType + EffectiveFrom are immutable on edit (they anchor the
  // supersession chain), so they're prefilled and disabled.
  const [feeType, setFeeType] = useState<TpsFeeType>(
    editRow?.feeType ?? "MonthlyServicingFee",
  );
  const [amountType, setAmountType] = useState<TpsFeeAmountType>(
    editRow?.amountType ?? "Flat",
  );
  // Collection mode is captured for every fee type; null on the wire means
  // deduct_from_payment.
  const [mode, setMode] = useState<string>(
    editRow?.mode ?? "deduct_from_payment",
  );
  // Edited as dollars (Flat) or percent (Percent); the percent is converted
  // to a fraction on save (and back to percent from the stored fraction here).
  const [amountText, setAmountText] = useState(() => {
    if (!editRow) return "";
    return editRow.amountType === "Percent"
      ? (editRow.amount * 100).toString()
      : editRow.amount.toString();
  });
  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    editRow ? isoDateInTz(editRow.effectiveFrom, "UTC") : "",
  );
  const [notes, setNotes] = useState(editRow?.notes ?? "");

  const parsedAmount = Number(amountText);
  const amountInvalid =
    amountText.trim() === "" || Number.isNaN(parsedAmount) || parsedAmount < 0;
  const dateInvalid = effectiveFrom.trim() === "";

  const submit = () => {
    if (amountInvalid || (!isEdit && dateInvalid)) return;
    const wireAmount =
      amountType === "Percent" ? parsedAmount / 100 : parsedAmount;
    const trimmedNotes = notes.trim() === "" ? null : notes.trim();

    if (isEdit && editRow) {
      update.mutate(
        {
          feeId: editRow.id,
          body: { amountType, amount: wireAmount, mode, notes: trimmedNotes },
        },
        {
          onSuccess: () => {
            toast.success("Fee row updated.");
            onClose();
          },
          onError: (err) => toast.error(extractErrorMessage(err)),
        },
      );
      return;
    }

    const iso = calendarDateInputToIso(effectiveFrom);
    if (!iso) {
      toast.error("Enter a valid effective-from date.");
      return;
    }
    create.mutate(
      {
        feeType,
        amountType,
        amount: wireAmount,
        effectiveFrom: iso,
        notes: trimmedNotes,
        mode,
      },
      {
        onSuccess: () => {
          toast.success("Fee row added.");
          setAmountText("");
          setEffectiveFrom("");
          setNotes("");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  const pending = isEdit ? update.isPending : create.isPending;

  return (
    <section
      className="rounded-lg border border-border-light bg-white-warm p-space-4"
      data-testid="tps-fee-add-form"
    >
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-3">
        {isEdit ? "Edit fee" : "Add a fee"}
      </h3>
      <div className="grid grid-cols-1 gap-space-3 md:grid-cols-2">
        <FormField label="Fee type" required>
          <Select
            value={feeType}
            onChange={(e) => setFeeType(e.target.value as TpsFeeType)}
            disabled={isEdit}
            aria-label="Fee type"
            data-testid="tps-fee-type"
          >
            {FEE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Amount type" required>
          <Select
            value={amountType}
            onChange={(e) =>
              setAmountType(e.target.value as TpsFeeAmountType)
            }
            aria-label="Amount type"
            data-testid="tps-fee-amount-type"
          >
            <option value="Flat">Flat (dollars)</option>
            <option value="Percent">Percent</option>
          </Select>
        </FormField>
        <FormField label="How is it collected?" required>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            aria-label="How is it collected?"
            data-testid="tps-fee-mode"
          >
            {FEE_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label={amountType === "Percent" ? "Amount (%)" : "Amount ($)"}
          required
        >
          <Input
            type="number"
            step={amountType === "Percent" ? "0.0001" : "0.01"}
            min={0}
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            invalid={amountText !== "" && amountInvalid}
            aria-label="Fee amount"
            data-testid="tps-fee-amount"
          />
        </FormField>
        <FormField label="Effective from" required>
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            disabled={isEdit}
            invalid={false}
            aria-label="Effective from"
            data-testid="tps-fee-effective-from"
          />
        </FormField>
        <FormField label="Notes" className="md:col-span-2">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            aria-label="Fee notes"
            data-testid="tps-fee-notes"
          />
        </FormField>
      </div>
      <div className="mt-space-3 flex justify-end">
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          pending={pending}
          pendingLabel={isEdit ? "Updating…" : "Adding…"}
          disabled={amountInvalid || (!isEdit && dateInvalid)}
          onClick={submit}
          data-testid="tps-fee-submit"
        >
          {isEdit ? "Update fee" : "Add fee"}
        </SubmitButton>
      </div>
    </section>
  );
}

function RecoveryRateForm({
  tpsId,
  currentRate,
}: {
  tpsId: string;
  currentRate: number | null;
}) {
  const toast = useToast();
  const setRate = useSetCollectionsRecoveryRate(tpsId);

  // Edited as a percent; the server stores a fraction. Empty input clears it.
  const [percent, setPercent] = useState(() =>
    currentRate === null ? "" : (currentRate * 100).toString(),
  );

  const trimmed = percent.trim();
  const parsed = Number(trimmed);
  const isClear = trimmed === "";
  // Server bound is [0,1) → [0%,100%).
  const outOfRange =
    !isClear && (Number.isNaN(parsed) || parsed < 0 || parsed >= 100);

  const save = () => {
    if (outOfRange) return;
    setRate.mutate(isClear ? null : parsed / 100, {
      onSuccess: () =>
        toast.success(
          isClear ? "Recovery rate cleared." : "Recovery rate saved.",
        ),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  };

  return (
    <section
      className="rounded-lg border border-border-light bg-white-warm p-space-4"
      data-testid="tps-recovery-rate-form"
    >
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-1">
        Collections-recovery rate
      </h3>
      <p className="text-caption text-gray-mid m-0 mb-space-3">
        Share of gross recovered amounts booked as a TPS success fee. Leave
        blank to clear.
      </p>
      <div className="flex items-end gap-space-3">
        <FormField label="Rate (%)" className="w-40">
          <div className="flex items-center gap-space-2">
            <Input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              invalid={outOfRange}
              aria-label="Collections-recovery rate percent"
              data-testid="tps-recovery-rate-input"
            />
            <span className="text-gray-mid">%</span>
          </div>
        </FormField>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          pending={setRate.isPending}
          pendingLabel="Saving…"
          disabled={outOfRange}
          onClick={save}
          data-testid="tps-recovery-rate-submit"
        >
          Save rate
        </SubmitButton>
      </div>
      {outOfRange && (
        <p className="mt-space-2 text-caption text-status-error" role="alert">
          Must be between 0% and 100%.
        </p>
      )}
    </section>
  );
}
